import { useState } from "preact/hooks";
import { API, api } from "../../api/client.ts";
import type { Member, Role, SpaceOut, UserKind } from "../../api/types.gen.ts";
import { type Key, t } from "../../i18n/index.ts";
import { presetField, uploadChosen } from "../../lib/agentPicture.ts";
import { type AgentPreset, type Client, clientFor, presetNamed } from "../../lib/agentPresets.ts";
import { agentRoles, managedBots } from "../../lib/agents.ts";
import { orgApi } from "../../lib/paths.ts";
import type { AgentPicture } from "../../lib/pictures.ts";
import { atLeast, roleOptions } from "../../lib/roles.ts";
import { me } from "../../lib/state.ts";
import { toast } from "../../lib/toast.ts";
import { Button, StepActions } from "../../ui/Button.tsx";
import { Dialog } from "../../ui/Dialog.tsx";
import { Checkbox, Field, Form, Input, Radios, Select } from "../../ui/form.tsx";
import { CopyButton } from "../../ui/misc.tsx";
import { AgentPictureChooser } from "../../ui/PictureEditor.tsx";
import { Steps } from "../../ui/Steps.tsx";
import { CreateKey } from "../ApiKeys.tsx";
import { NoPicture, PresetChips } from "./agentForm.tsx";

const CLIENTS: [Client, Key][] = [
  ["agent", "connect.client.agent"],
  ["claude-code", "connect.client.claudeCode"],
  ["cursor", "connect.client.cursor"],
  ["vscode", "connect.client.vscode"],
  ["codex", "connect.client.codex"],
  ["other", "connect.client.other"],
];

/** Where each client keeps the snippet (null: it's a message or a command). */
const WHERE: Record<Client, Key | null> = {
  agent: "connect.where.agent",
  "claude-code": "connect.where.claudeCode",
  cursor: "connect.where.cursor",
  vscode: "connect.where.vscode",
  codex: "connect.where.codex",
  other: null,
};

/** A client's name, for a key's: the ones with a setup of their own (none for any agent or another). */
const clientName = (c: Client): string | undefined => {
  const label = CLIENTS.find(([k]) => k === c)?.[1];
  return label && c !== "agent" && c !== "other" ? t(label) : undefined;
};

const json = (v: unknown): string => JSON.stringify(v, null, 2);

/** The setup for `client`, connecting to `url` with `key`. */
function snippet(client: Client, url: string, key: string): string {
  const auth = `Bearer ${key}`;
  switch (client) {
    case "agent":
      return t("connect.prompt", { url, key });
    case "claude-code":
      return `claude mcp add --transport http revisa ${url} --header "Authorization: ${auth}"`;
    case "cursor":
      return json({ mcpServers: { revisa: { url, headers: { Authorization: auth } } } });
    case "vscode":
      return json({ servers: { revisa: { type: "http", url, headers: { Authorization: auth } } } });
    case "codex":
      return `[mcp_servers.revisa]\nurl = "${url}"\nhttp_headers = { "Authorization" = "${auth}" }`;
    default:
      return `URL: ${url}\nAuthorization: ${auth}`;
  }
}

type Source = "new" | "existing";

const STEPS = ["choose", "key", "setup"] as const;
type Step = (typeof STEPS)[number] | "agent";
/** Adding an agent: naming it first (instead of choosing how to get its key). */
const ADDING: readonly Step[] = ["agent", "key", "setup"];

const stepLabels = (source: Source): string[] => [
  t("connect.step.choose"),
  t(source === "new" ? "connect.step.create" : "connect.step.paste"),
  t("connect.step.setup"),
];

/** Step 3: the setup for the picked client, with the key filled in. */
function Setup(p: { apiKey: string; created: boolean; client: Client; onRestart: () => void }) {
  const { apiKey, created } = p;
  const [client, setClient] = useState<Client>(p.client);
  const where = WHERE[client];
  const text = snippet(client, `${API}/mcp`, apiKey);
  return (
    <>
      {created ? (
        <p class="mb-4 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-900">{t("connect.keyCreated")}</p>
      ) : null}
      <Select
        compact={true}
        aria-label={t("connect.clients")}
        value={client}
        onValue={(v) => setClient(v as Client)}
        options={CLIENTS.map(([c, label]) => ({ value: c, label: t(label) }))}
      />
      {where ? <p class="mb-2 text-sm text-zinc-600">{t(where)}</p> : null}
      <pre class="overflow-x-auto rounded-md bg-zinc-100 p-3 text-sm whitespace-pre-wrap break-words">{text}</pre>
      <StepActions>
        <CopyButton text={text} label={t("common.copy")} variant="primary" />
        <Button variant="ghost" onClick={p.onRestart}>
          {t("connect.another")}
        </Button>
      </StepActions>
    </>
  );
}

/** A new agent or integration as named in the first step: made only with its key, in the next. */
interface AgentDraft {
  kind: UserKind;
  name: string;
  role: Role;
  /** Yours from the start (agents only). */
  personal: boolean;
  picture: AgentPicture;
  preset: AgentPreset | undefined;
}

const NO_DRAFT: AgentDraft = {
  kind: "agent",
  name: "",
  role: "editor",
  personal: false,
  picture: { kind: "none" },
  preset: undefined,
};

/** Makes `draft` a member of the organization (its picture too): its id. */
async function makeAgent(space: SpaceOut, draft: AgentDraft): Promise<string> {
  const { kind, name, role, picture } = draft;
  const mine = kind === "agent" && draft.personal;
  const body = { kind, name, role, ...(mine ? { personal: true } : {}), ...presetField(picture, { kind: "none" }) };
  const made = await api.post<Member>(orgApi(space, "/members"), body);
  await uploadChosen(space, made.id, picture);
  toast(t("members.added"));
  return made.id;
}

/**
 * Naming a new agent or integration, with its role and picture: the first step of adding one (nothing's made yet:
 * back from the next, it's as it was left). An agent can start from a known one: its name and picture.
 */
function NewAgent({
  space,
  draft,
  onNext,
  onCancel,
}: {
  space: SpaceOut;
  draft: AgentDraft;
  onNext: (draft: AgentDraft) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(draft.name);
  const [kind, setKind] = useState<UserKind>(draft.kind);
  const [role, setRole] = useState<Role>(draft.role);
  // Yours from the start: it sorts your private triage area (agents only).
  const [personal, setPersonal] = useState(draft.personal);
  // Admins add integrations too; editors set up agents, up to their own role.
  const admin = atLeast(space.role, "admin");
  const [preset, setPreset] = useState<AgentPreset | undefined>(draft.preset);
  const [picture, setPicture] = useState<AgentPicture>(draft.picture);
  const pick = (p?: AgentPreset) => {
    setPreset(p);
    setName(p?.name ?? "");
    setPicture(p ? { kind: "preset", preset: p.picture } : { kind: "none" });
  };
  return (
    <Form class="flex flex-col gap-4" onSubmit={() => onNext({ kind, name, role, personal, picture, preset })}>
      {kind === "agent" ? <PresetChips value={preset} onPick={pick} /> : null}
      <Field label={t("members.agentName")}>
        <Input value={name} onValue={setName} required={true} />
      </Field>
      <div class="grid gap-4 sm:grid-cols-2">
        {admin ? (
          <Field label={t("members.kind")}>
            <Select
              value={kind}
              onValue={(v) => setKind(v as UserKind)}
              options={(["agent", "integration"] as const).map((k) => ({ value: k, label: t(`userKind.${k}`) }))}
            />
          </Field>
        ) : null}
        <Field label={t("members.role")}>
          <Select value={role} onValue={(v) => setRole(v as Role)} options={roleOptions(agentRoles(space.role))} />
        </Field>
      </div>
      {kind === "agent" && me.value?.kind === "human" ? (
        <div class="flex flex-col gap-1">
          <Checkbox checked={personal} onCheck={setPersonal} label={t("personal.makeMine")} />
          <p class="text-xs text-zinc-500">{t("agents.personalHint")}</p>
        </div>
      ) : null}
      <AgentPictureChooser value={picture} onValue={setPicture} fallback={<NoPicture name={name} kind={kind} />} />
      <StepActions>
        <Button answer="cancel" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" variant="primary" submit={true}>
          {t("common.continue")}
        </Button>
      </StepActions>
    </Form>
  );
}

interface ConnectProps {
  space: SpaceOut;
  agent: string | null;
  adding?: boolean;
  members: readonly Member[] | undefined;
  onChange: () => void;
  onClose: () => void;
}

/**
 * Connecting an agent, in a dialog: a new key or an existing one; creating it (as an agent, picked or new) or pasting
 * it; then the setup to copy per client. Given `agent` (its row in Members), for that agent. `adding` one: naming it
 * first, then its key and setup, in the same steps. `members` are the organization's; `onChange` hears of one added.
 */
export function ConnectAgent(p: ConnectProps & { open: boolean }) {
  const name = p.members?.find((m) => m.id === p.agent)?.name;
  const title = p.adding ? t("members.addAgent") : name ? t("connect.titleFor", { name }) : t("connect.title");
  return (
    <Dialog open={p.open} onClose={p.onClose} title={title} wide={true}>
      <ConnectSteps {...p} />
    </Dialog>
  );
}

function ConnectSteps({ space, agent, adding = false, members, onChange, onClose }: ConnectProps) {
  const [who, setWho] = useState(agent);
  const [step, setStep] = useState<Step>(adding ? "agent" : agent ? "key" : "choose");
  const [source, setSource] = useState<Source>("new");
  const [created, setCreated] = useState("");
  const [pasted, setPasted] = useState("");
  // The setup shown first: the added agent's client's, when it's a known one.
  const [client, setClient] = useState<Client>("agent");
  // The agent being added, as named (made with its key); once made (its key not yet: trying again), its id.
  const [draft, setDraft] = useState<AgentDraft>(NO_DRAFT);
  const [made, setMade] = useState<string | null>(null);
  const restart = () => {
    setCreated("");
    setPasted("");
    setStep("choose");
  };
  const steps = adding ? ADDING : STEPS;
  const labels = adding
    ? [t("connect.step.agent"), t("connect.step.create"), t("connect.step.setup")]
    : stepLabels(source);
  return (
    <>
      <p class="max-w-2xl text-sm text-zinc-700">{t("connect.intro")}</p>
      <Steps labels={labels} current={steps.indexOf(step)} />
      {step === "agent" ? (
        <NewAgent
          space={space}
          draft={draft}
          onCancel={onClose}
          onNext={(d) => {
            setDraft(d);
            // Named otherwise after it was made (its key failing): another.
            setMade(null);
            setClient(clientFor(d.preset ?? presetNamed(d.name)));
            setStep("key");
          }}
        />
      ) : null}
      {step === "choose" ? (
        <>
          <Radios
            label={t("connect.step.choose")}
            value={source}
            onValue={setSource}
            options={[
              { value: "new", label: t("connect.newKey") },
              { value: "existing", label: t("connect.existingKey") },
            ]}
          />
          <StepActions>
            <Button variant="primary" onClick={() => setStep("key")}>
              {t("common.continue")}
            </Button>
          </StepActions>
        </>
      ) : null}
      {step === "key" && source === "new" ? (
        <>
          <CreateKey
            space={space}
            bots={managedBots(members, me.value?.id, space.role)}
            agentFirst={true}
            client={clientName(client)}
            {...(who ? { initialUser: who } : {})}
            // Being added: made with its key, back to naming it until then.
            {...(adding
              ? {
                  adding: {
                    name: draft.name,
                    make: async () => {
                      const id = made ?? (await makeAgent(space, draft));
                      setMade(id);
                      setWho(id);
                      onChange();
                      return id;
                    },
                  },
                }
              : {})}
            onBack={adding ? () => setStep("agent") : () => setStep("choose")}
            onCreated={(secret) => {
              setCreated(secret);
              setStep("setup");
            }}
          />
          {atLeast(space.role, "admin") || who ? null : <p class="text-sm text-zinc-600">{t("connect.asYou")}</p>}
        </>
      ) : null}
      {step === "key" && source === "existing" ? (
        <Form onSubmit={() => setStep("setup")}>
          <Field label={t("connect.key")}>
            <Input value={pasted} onValue={setPasted} placeholder="rvk_…" required={true} />
          </Field>
          <StepActions onBack={() => setStep("choose")}>
            <Button variant="primary" submit={true} disabled={pasted.trim() === ""}>
              {t("common.continue")}
            </Button>
          </StepActions>
        </Form>
      ) : null}
      {step === "setup" ? (
        <Setup
          apiKey={(source === "new" ? created : pasted).trim()}
          created={source === "new"}
          client={client}
          onRestart={restart}
        />
      ) : null}
    </>
  );
}
